import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { POLL_INTERVAL_MS } from './constants';
import { operationsErrorMessages } from './helpers';

/**
 * XBIT-4870: one Operations list. Read on mount and whenever `deps` change, then every POLL_INTERVAL_MS while the tab is
 * visible (silently: no loading flag, and a failed poll keeps the rows it has). A read that lands after a newer one began
 * is dropped. `refetch` reads at once and resolves when the read has landed. With `enabled` false (the role lacks the permission
 * of this list) nothing is read, polled or refetched, and `data` stays null.
 */
export const useQueue = <T>(
  read: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
  enabled = true
) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useReconciliation).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const readRef = useRef(read);
  readRef.current = read;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const latest = useRef(0);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [readAt, setReadAt] = useState<Date | null>(null);

  const load = useCallback(async (quiet: boolean) => {
    if (!enabledRef.current) return;
    latest.current += 1;
    const call = latest.current;
    if (!quiet) setLoading(true);
    try {
      const next = await readRef.current();
      if (call !== latest.current) return;
      setData(next);
      setError(null);
      setReadAt(new Date());
    } catch (e) {
      if (call !== latest.current) return;
      // A failed poll keeps what is on screen; a failed first or manual read shows the error.
      if (!quiet) {
        setData(null);
        setError(operationsErrorMessages(e, translateRef.current)[0]);
      }
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => load(false), [load]);

  useEffect(() => {
    load(false);
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') load(true);
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      latest.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, enabled, ...deps]);

  return { data, error, loading, readAt, refetch };
};

export type Queue<T> = ReturnType<typeof useQueue<T>>;
